import { kebabCase } from "es-toolkit";

import { radiusScaleDeclarations } from "../canvas/radiusScale.ts";
import { THEME_COLOR_TOKEN_NAMES, type ColorTokens } from "../canvas/theme.ts";

import {
  type ElevationTokens,
  FONT_ROLES,
  THEME_APPEARANCE_ATTRIBUTE,
  type Theme,
  type ThemeMode,
  type TypeScale,
  type TypographyTokens,
  fontFamilyStack,
  themeColors,
  themeElevation,
} from "./theme.ts";

/**
 * A theme, as one stylesheet.
 *
 * Both modes ship together: the mode-independent groups and the light colours
 * at `:root`, the dark colours behind {@link THEME_APPEARANCE_ATTRIBUTE}. So an
 * appearance swap is one attribute on the document rather than a recompiled
 * sheet, and the compiled string can be cached per theme instead of per screen
 * per appearance.
 *
 * The shape is shadcn's own — literals at `:root`, a `@theme inline` block
 * mapping the colour namespace onto them — for two reasons beyond familiarity.
 * The mapping is what makes `bg-primary` compile at all, since Tailwind has no
 * such colour of its own; and because it is `inline`, the utility resolves to
 * `var(--primary)` rather than to a copy of the value, which is what lets the
 * dark block move a component the utility was compiled for. Every OTHER group
 * — radius, spacing, fonts, and the ramps a theme may override — is a name
 * Tailwind already declares, so a plain unlayered `:root` value is enough to
 * win, and `@theme` would only hide the variable from the browser.
 */
export const compileTheme = (theme: Theme): string =>
  [
    DARK_VARIANT,
    cssBlock(LIGHT_SELECTOR, [
      ...modeDeclarations(theme, "light"),
      "",
      ...radiusScaleDeclarations(theme.radius.base),
      `--spacing: ${theme.spacing.base};`,
      ...typographyDeclarations(theme.typography),
      ...elevationDeclarations(themeElevation(theme, "light")),
    ]),
    cssBlock(DARK_SELECTOR, modeDeclarations(theme, "dark")),
    // What makes `bg-primary` a class Tailwind will compile at all.
    cssBlock("@theme inline", colorAliasDeclarations()),
    DEFAULT_BORDER_COLOR,
  ].join("\n\n    ");

/**
 * Where each mode's tokens land.
 *
 * The attribute is matched on ANY element, not only `:root`, because a screen's
 * mode is not always the document's: code export puts every screen of a
 * prototype in one routed document, so the mode has to ride on the screen's own
 * wrapper. Custom properties inherit, so an element carrying the attribute
 * re-declares the mode for its subtree — which also means a dark section inside
 * a light screen is expressible rather than a special case.
 *
 * The two selectors have equal specificity, so ORDER is what decides an element
 * that matches both — `:root` with the attribute on it, our own iframes. Dark
 * comes second and wins. Light claims `[data-appearance="light"]` for the same
 * symmetry: it is what makes a light island inside a dark subtree work, and the
 * attribute is written out for light screens too.
 */
/**
 * Tailwind's own `dark:` is `prefers-color-scheme` — the viewer's OS — which
 * would miss a dark screen on a light machine and hit a light screen on a dark
 * one. Twelve of the components we render carry `dark:` classes.
 *
 * `:is(...)` because the attribute sits on the element itself in an exported
 * screen's wrapper, and on an ancestor in our iframes.
 */
const DARK_VARIANT = `@custom-variant dark (&:is([${THEME_APPEARANCE_ATTRIBUTE}="dark"], [${THEME_APPEARANCE_ATTRIBUTE}="dark"] *));`;

const LIGHT_SELECTOR = `:root, [${THEME_APPEARANCE_ATTRIBUTE}="light"]`;
const DARK_SELECTOR = `[${THEME_APPEARANCE_ATTRIBUTE}="dark"]`;

/**
 * The one rule that belongs to a SCREEN rather than to its theme.
 *
 * `h-screen` inside a design frame means the frame, not the window, so it is
 * pinned to the artboard's own height. That makes it the one piece of a
 * screen's stylesheet that cannot be shared: fold it into a compiled theme and
 * two screens of different heights wearing one theme will disagree about which
 * height won — which is why it is separable, and why code export keeps it in
 * each screen's own file.
 */
export const screenHeightClampCss = (defaultHeight: number): string =>
  hacks(defaultHeight);

/** The reset every screen renders under, whatever its theme or size. */
export const screenResetCss = (): string => NORMALIZE;

/** The reset every theme preview renders under. */
export const themePreviewResetCss = (): string => NORMALIZE_THEME_PREVIEW;
/**
 * The reset and the height clamp: everything a screen's document needs that is
 * not the theme.
 *
 * Kept out of {@link compileTheme} because the clamp depends on the screen, and
 * a per-screen ingredient in there would make the compiled theme uncacheable
 * for the sake of two rules. For a document holding ONE screen — every canvas
 * iframe, the screenshot renderer, the prototype — the two travel together.
 */
export const screenBaseCss = (defaultHeight: number): string =>
  `${screenHeightClampCss(defaultHeight)}\n    ${screenResetCss()}`;

/** The tokens that differ between the two modes. */
const modeDeclarations = (theme: Theme, mode: ThemeMode): string[] => [
  ...colorDeclarations(themeColors(theme, mode)),
  "",
  ...colorAliasDeclarations(),
  ...elevationDeclarations(themeElevation(theme, mode)),
];

const colorDeclarations = (colors: ColorTokens): string[] =>
  THEME_COLOR_TOKEN_NAMES.map(
    (token) => `--${kebabCase(token)}: ${colors[token]};`,
  );

/**
 * `--color-primary: var(--primary)`, at browser level.
 *
 * The same mapping the `@theme inline` block makes, declared a second time as
 * plain CSS because an `inline` block's namespaced variables are substituted
 * into utilities and never emitted — so hand-written CSS reading
 * `var(--color-border)` would resolve to nothing. {@link DEFAULT_BORDER_COLOR}
 * below does exactly that, and so does the rule inspector when it paints a
 * tokened colour.
 *
 * Emitted in BOTH mode blocks rather than once at `:root`, which is not
 * redundancy: a custom property whose value contains `var()` is substituted on
 * the element it is DECLARED on, and what inherits is the result. An alias
 * declared only at `:root` therefore freezes to the light value — so a subtree
 * re-declaring `--primary`, an exported screen wearing dark inside a light
 * document, would move the utilities and leave every alias reader behind.
 */
const colorAliasDeclarations = (): string[] =>
  THEME_COLOR_TOKEN_NAMES.map((token) => {
    const name = kebabCase(token);
    return `--color-${name}: var(--${name});`;
  });

const typographyDeclarations = (typography: TypographyTokens): string[] => [
  ...FONT_ROLES.map(
    (role) => `--font-${role}: ${fontFamilyStack(typography.families[role])};`,
  ),
  // The role body text takes. Tailwind's preflight would reach the same value
  // through `--default-font-family`, which is `var(--font-sans)` — but only for
  // a theme whose default role IS sans.
  `font-family: var(--font-${typography.families.default});`,
  ...typeScaleDeclarations(typography.scale),
];

/**
 * The `--text-*`, `--leading-*` and `--tracking-*` steps the theme moves.
 *
 * Nothing states any yet, so every one of these stays Tailwind's — which is
 * what paints them today. Step 9 fills the scale in; the emit path is here so
 * that it can.
 */
const typeScaleDeclarations = (scale: TypeScale | undefined): string[] => {
  if (!scale) return [];

  return [
    ...Object.entries(scale.size ?? {}).flatMap(([name, step]) => [
      `--text-${name}: ${step.size};`,
      ...(step.lineHeight === undefined
        ? []
        : [`--text-${name}--line-height: ${step.lineHeight};`]),
    ]),
    ...Object.entries(scale.leading ?? {}).map(
      ([name, value]) => `--leading-${name}: ${value};`,
    ),
    ...Object.entries(scale.tracking ?? {}).map(
      ([name, value]) => `--tracking-${name}: ${value};`,
    ),
  ];
};

/** As above, and per mode: a dark surface wants its own alpha, not an inverted one. */
const elevationDeclarations = (
  elevation: ElevationTokens | undefined,
): string[] => [
  ...Object.entries(elevation?.shadow ?? {}).map(
    ([name, value]) => `--shadow-${name}: ${value};`,
  ),
  ...Object.entries(elevation?.blur ?? {}).map(
    ([name, value]) => `--blur-${name}: ${value};`,
  ),
];

/** A selector and its declarations, or nothing when there are none. */
const cssBlock = (selector: string, declarations: string[]): string =>
  declarations.length === 0
    ? ""
    : `${selector} {
        ${declarations.join("\n        ")}
    }`;

const NORMALIZE = `* {
        scrollbar-width: none;
    }

    *::-webkit-scrollbar {
        display: none;
    }

    body {
        font-size: 16px;
        line-height: 24px;
        letter-spacing: 0;
        padding: 0;
        margin: 0;
    }
    `;

const NORMALIZE_THEME_PREVIEW = `
    * {
        scrollbar-width: none;
    }

    *::-webkit-scrollbar {
        display: none;
    }

    body {
        font-size: 16px;
        line-height: 24px;
        letter-spacing: 0;
        padding: 0;
        margin: 0;
    }

    html, body {
        width: 100%;
        height: 100%;
    }

    .frame-content {
        width: 100%;
        height: 100%;
    }
`;

const hacks = (defaultHeight: number) => `
    /* limit the size of max height elements */
    .h-screen {
      height: ${defaultHeight}px !important;
    }
    .min-h-screen {
      min-height: ${defaultHeight}px !important;
    }
    `;

/**
 * A bare `border` paints the theme's border colour, as under shadcn, where
 * Tailwind v4 alone leaves it `currentcolor`.
 *
 * In `@layer base`, after preflight's reset: an unlayered rule here outranks
 * every utility, so `hover:border-primary` or `border-red-500` would never
 * paint.
 */
const DEFAULT_BORDER_COLOR = `@layer base {
        *, ::after, ::before, ::backdrop, ::file-selector-button {
            border-color: var(--color-border, currentcolor);
        }
    }`;
